// Public downloads use format-specific projections, never API/owner object spreads.
// Text is intentionally not a PII detector: names and published labels are free text.
const scalar = value => typeof value === "string" || (typeof value === "number" && Number.isFinite(value)) ? value : undefined;
export const publicExportText = value => String(scalar(value) ?? "");
const fields = (source, names) => Object.fromEntries(names.flatMap(name => {
  const value = scalar(source?.[name]);
  return value === undefined ? [] : [[name, value]];
}));
const list = (value, project) => Array.isArray(value) ? value.map(project) : [];
const flag = value => value === true;

// Competitor number is a public match-local number, NOT an NRA membership or
// competitor ID. Never fall back to an identity, registration or membership ID.
const bulletinRow = row => ({
  ...fields(row, ["competitorName", "competitorNumber", "classificationLabel", "divisionLabel",
    "rank", "awardRank", "totalScore", "xCount", "tieBreakRule", "awardText", "awardNote"]),
  isTied: flag(row?.isTied),
});
const bulletinBlock = block => ({
  ...fields(block, ["title", "awardSummary", "placeLabel", "emptyTitle"]),
  rows: list(block?.rows, bulletinRow),
  children: list(block?.children, bulletinBlock),
});

export function sanitizePublicBulletinExport(options) {
  return {
    // programNotes and awardNote are published class-award policy/decisions,
    // not participant, registration, scorecard or audit notes.
    ...fields(options, ["matchId", "name", "date", "location", "programNotes"]),
    sections: list(options?.sections, section => ({
      ...fields(section, ["title", "participationLabel"]),
      blocks: list(section?.blocks, bulletinBlock),
    })),
  };
}

export function sanitizePublicStandingsExport(options) {
  return {
    ...fields(options, ["matchId", "name", "date", "location", "matchStatus"]),
    includeClassification: flag(options?.includeClassification),
    includeShots: flag(options?.includeShots),
    sections: list(options?.sections, section => ({
      ...fields(section, ["label", "filterLabel"]),
      rows: list(section?.rows, row => ({
        ...fields(row, ["name", "competitorNumber", "participation", "rank", "division",
          "classification", "progressLabel", "scoreLabel"]),
        tied: flag(row?.tied),
      })),
    })),
  };
}

export function sanitizePublicRelayExport(options) {
  const match = options?.match;
  return {
    match: {
      // Venue addresses and match dates are public, unlike competitor addresses
      // and birth dates. Keep the venue fields scoped to the match only.
      ...fields(match, ["matchId", "matchName", "startDate", "matchDate", "endDate", "timeZone",
        "rangeName", "rangeAddress", "location", "city", "state", "postalCode", "country"]),
      operations: { relays: list(match?.operations?.relays, relay => fields(relay, ["label"])) },
    },
    roster: { competitors: list(options?.roster?.competitors, person => ({
      ...fields(person, ["displayName", "rosterName", "competitorNumber"]),
      assignments: list(person?.assignments, assignment => ({
        ...fields(assignment, ["relayLabel", "firingPointLabel", "groupTitle", "division"]),
        twentyTwoOnly: flag(assignment?.twentyTwoOnly),
      })),
    })) },
  };
}

const scoreFields = ["totalScore", "xCount", "shotsFired"];
export function sanitizePublicAwardsRows(rows) {
  return list(rows, row => ({
    ...fields(row, ["competitorName", "competitorNumber", ...scoreFields]),
    firearms: list(row?.firearms, firearm => ({
      ...fields(firearm, ["gunGroupId", "firearmTitle", ...scoreFields]),
      courses: list(firearm?.courses, course => fields(course, ["courseName", ...scoreFields])),
    })),
  }));
}
